import { useEffect, useState, type DragEvent, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';
import { ChevronLeft, ImagePlus, X } from 'lucide-react';
import { ContactFields } from '../components/ContactFields';
import { useCompanyFeatures } from '../hooks/useCompanyFeatures';
import { contactErrors, contactPayload, EMPTY_CONTACTS, type ContactForm } from '../lib/contacts';
import { CustomerPicker } from '../components/CustomerPicker';
import { BookingFields } from '../components/BookingFields';
import { PhotoTypeTag } from '../components/JobTags';
import { Button, buttonStyles } from '../components/ui/Button';
import { Field, OutstandingMark, Select, TextArea, TextInput, useFieldId } from '../components/ui/Field';
import { SegmentedControl } from '../components/ui/SegmentedControl';
import { Switch } from '../components/ui/Switch';
import { Alert } from '../components/ui/Alert';
import { useToast } from '../components/ui/Toast';
import { RegPlate } from '../components/ui/RegPlate';
import { useAuth } from '../auth/AuthProvider';
import { db, functions } from '../lib/firebase';
import { usePageTitle } from '../hooks/usePageTitle';
import { uploadJobImage, type WebImageType } from '../lib/imageUpload';
import { evidenceUploadTypes } from '../lib/evidence';
import { withReadTimeout } from '../lib/readLoading';
import { FUEL_TYPES, TRANSMISSIONS, pickerOptions } from '../lib/jobPickers';
import { createJob as createJobCallable } from '../lib/jobsBackend';
import { missingCompletionFields, type CompletionField } from '../lib/completion';
import { bookingFields, bookingWindowIsBackwards, EMPTY_BOOKING, type BookingForm } from '../lib/schedule';
import {
  canBookJobs, canDispatchJobs, canCreateJobs, type CompanyMember, type Customer,
  type SubcontractorRecord, type VehicleLookupResponse,
} from '../types/models';

interface JobForm extends ContactForm {
  registrationNumber: string; make: string; model: string; colour: string;
  fuelType: string; transmission: string; customerName: string;
  pickupLocation: string; dropoffLocation: string; notes: string;
  vehicleMileage: string; courtesyCarRegistration: string; courtesyCarMileage: string;
}
type AssignmentMode = 'clear' | 'internal' | 'subcontractor';
type LookupState = 'idle' | 'filled' | 'failed';

const ASSIGNMENT_OPTIONS: ReadonlyArray<{ value: AssignmentMode; label: string }> = [
  { value: 'clear', label: 'Unassigned' },
  { value: 'internal', label: 'Team member' },
  { value: 'subcontractor', label: 'Subcontractor' },
];

const INITIAL_FORM: JobForm = {
  ...EMPTY_CONTACTS,
  registrationNumber: '', make: '', model: '', colour: '', fuelType: '', transmission: '',
  customerName: '', pickupLocation: '', dropoffLocation: '', notes: '', vehicleMileage: '',
  courtesyCarRegistration: '', courtesyCarMileage: '',
};

export default function NewJobPage() {
  usePageTitle('New job');
  const navigate = useNavigate();
  const toast = useToast();
  const { activeCompanyId, activeRole, user } = useAuth();
  const [draftJobId] = useState(getOrCreateDraftJobId);
  const companyFeatures = useCompanyFeatures();
  const [form, setForm] = useState<JobForm>(INITIAL_FORM);
  const [courtesyCarRequired, setCourtesyCarRequired] = useState(false);
  // When the job is booked for (PR-43); Not set = ASAP on Live Jobs.
  const [booking, setBooking] = useState<BookingForm>(EMPTY_BOOKING);
  const [files, setFiles] = useState<File[]>([]);
  const [imageType, setImageType] = useState<WebImageType>('collection');
  const [isDraggingPhotos, setIsDraggingPhotos] = useState(false);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [customerError, setCustomerError] = useState<string | null>(null);
  const [assignmentMode, setAssignmentMode] = useState<AssignmentMode>('clear');
  const [members, setMembers] = useState<CompanyMember[]>([]);
  const [subcontractors, setSubcontractors] = useState<SubcontractorRecord[]>([]);
  const [selectedAssignedUserId, setSelectedAssignedUserId] = useState('');
  const [selectedAssignedSubcontractorId, setSelectedAssignedSubcontractorId] = useState('');
  const [loadingAssignmentOptions, setLoadingAssignmentOptions] = useState(false);
  const [assignmentError, setAssignmentError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [lookingUp, setLookingUp] = useState(false);
  const [lookup, setLookup] = useState<LookupState>('idle');
  const [uploadProgress, setUploadProgress] = useState<{ done: number; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const canCreate = canCreateJobs(activeRole ?? undefined);
  const canAssign = canDispatchJobs(activeRole ?? undefined);
  // Drivers may book their own new jobs too (Bobby, 1 Nov 2026).
  const canBook = canBookJobs(activeRole ?? undefined);
  const registration = form.registrationNumber.trim().toUpperCase();
  const assignmentValid =
    !canAssign || assignmentMode === 'clear' ||
    (assignmentMode === 'internal' && !!selectedAssignedUserId) ||
    (assignmentMode === 'subcontractor' && !!selectedAssignedSubcontractorId);
  const isValid = registration.length > 0 && canCreate && assignmentValid && !!activeCompanyId && !!user
    && !bookingWindowIsBackwards(booking) && Object.keys(contactErrors(form, companyFeatures.contactEmailEnabled)).length === 0;

  // Amber = required before the server will let the job complete, not yet supplied.
  // The registration is required to *create*, so it is never shown as outstanding.
  const missing = new Set<CompletionField>(
    missingCompletionFields({ ...form, registrationNumber: registration, courtesyCarRequired })
      .map((item) => item.field)
      .filter((field) => field !== 'registrationNumber')
  );
  const owed = (field: CompletionField) => missing.has(field);
  const countOwed = (fields: CompletionField[]) => fields.filter(owed).length;
  const vehicleOwed = countOwed(['make', 'model', 'colour', 'fuelType', 'transmission']);
  const routeOwed = countOwed(['customerName', 'pickupLocation', 'dropoffLocation']);
  const courtesyOwed = countOwed(['courtesyCarRegistration']);
  const createBlockedReason = !registration
    ? 'registration needed'
    : !assignmentValid
      ? assignmentMode === 'internal' ? 'choose a team member' : 'choose a subcontractor'
      : null;
  const ids = {
    make: useFieldId('make'), model: useFieldId('model'), colour: useFieldId('colour'),
    fuel: useFieldId('fuel'), transmission: useFieldId('transmission'), customer: useFieldId('customer'),
    pickup: useFieldId('pickup'), dropoff: useFieldId('dropoff'), assignee: useFieldId('assignee'),
    courtesyReg: useFieldId('courtesy-reg'), vehicleMileage: useFieldId('vehicle-mileage'),
    courtesyMileage: useFieldId('courtesy-mileage'), notes: useFieldId('notes'),
  };

  useEffect(() => {
    if (!activeCompanyId || !canAssign) { setMembers([]); setSubcontractors([]); return; }
    let cancelled = false;
    setLoadingAssignmentOptions(true);
    setAssignmentError(null);
    const loadMembers = withReadTimeout(getDocs(query(collection(db, 'companies', activeCompanyId, 'members'), orderBy('displayName'))))
      .then((snap) => snap.docs
        .map((memberDoc) => {
          const data = memberDoc.data() as Omit<CompanyMember, 'id'>;
          return { id: memberDoc.id, ...data, userId: data.userId || memberDoc.id, companyId: activeCompanyId } as CompanyMember;
        })
        .filter((m) => m.isActive !== false && !['subcontractor', 'accounts', 'viewer'].includes(m.role)))
      .catch((e) => {
        console.warn('Failed to load assignment members', e);
        if (!cancelled) setAssignmentError('Internal users are unavailable for this role. You can still create the job unassigned or assign a subcontractor.');
        return [] as CompanyMember[];
      });
    const loadSubcontractors = withReadTimeout(getDocs(query(collection(db, 'companies', activeCompanyId, 'subcontractors'), orderBy('name'))))
      .then((snap) => snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<SubcontractorRecord, 'id'>) })).filter((s) => s.isActive !== false))
      .catch((e) => {
        console.warn('Failed to load subcontractors', e);
        if (!cancelled) setAssignmentError('Assignment options could not be loaded. You can still create the job unassigned.');
        return [] as SubcontractorRecord[];
      });
    void Promise.all([loadMembers, loadSubcontractors]).then(([nextMembers, nextSubs]) => {
      if (cancelled) return;
      setMembers(nextMembers); setSubcontractors(nextSubs); setLoadingAssignmentOptions(false);
    });
    return () => { cancelled = true; };
  }, [activeCompanyId, canAssign]);

  useEffect(() => {
    if (!activeCompanyId) { setCustomers([]); return; }
    let cancelled = false;
    setCustomerError(null);
    void withReadTimeout(getDocs(query(collection(db, 'companies', activeCompanyId, 'customers'), orderBy('name'))))
      .then((snap) => {
        if (cancelled) return;
        setCustomers(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Customer, 'id'>) })).filter((c) => c.isActive !== false));
      })
      .catch((e) => {
        console.warn('Failed to load customers', e);
        if (!cancelled) { setCustomerError('Customer picker could not be loaded.'); setCustomers([]); }
      });
    return () => { cancelled = true; };
  }, [activeCompanyId]);

  function setField(key: keyof JobForm, value: string) { setForm((c) => ({ ...c, [key]: value })); }

  function chooseAssignmentMode(next: AssignmentMode) {
    setAssignmentMode(next);
    if (next === 'internal' && !selectedAssignedUserId && members[0]) setSelectedAssignedUserId(members[0].userId);
    if (next === 'subcontractor' && !selectedAssignedSubcontractorId && subcontractors[0]) setSelectedAssignedSubcontractorId(subcontractors[0].id);
  }
  function chooseCustomer(value: string, customer?: Customer) {
    setField('customerName', value);
    const address = customer?.companyAddress?.trim();
    const currentDropoff = form.dropoffLocation.trim();
    if (address && (!currentDropoff || currentDropoff === 'Unknown')) setField('dropoffLocation', address);
  }
  function addPhotoFiles(next: File[]) {
    const imageFiles = next.filter((f) => f.type.startsWith('image/'));
    if (imageFiles.length === 0) return;
    setFiles((current) => {
      const existing = new Set(current.map(fileKey));
      return [
        ...current,
        ...imageFiles.filter((file) => !existing.has(fileKey(file))),
      ];
    });
  }
  function handlePhotoDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault(); setIsDraggingPhotos(false);
    addPhotoFiles(Array.from(event.dataTransfer.files));
  }

  async function lookupRegistration() {
    if (registration.length < 2) return;
    setLookingUp(true); setError(null);
    try {
      const call = httpsCallable<{ registration: string }, VehicleLookupResponse>(functions, 'lookupVehicle');
      const res = await call({ registration });
      const dvla = (res.data.dvla ?? {}) as Record<string, unknown>;
      setForm((c) => ({
        ...c, registrationNumber: registration,
        make: res.data.motVehicleInfo?.make ?? stringFromUnknown(dvla.make) ?? c.make,
        model: res.data.motVehicleInfo?.model ?? stringFromUnknown(dvla.model) ?? c.model,
        colour: res.data.motVehicleInfo?.primaryColour ?? stringFromUnknown(dvla.colour) ?? c.colour,
        fuelType: res.data.motVehicleInfo?.fuelType ?? stringFromUnknown(dvla.fuelType) ?? c.fuelType,
      }));
      setLookup('filled');
    } catch (e) {
      // A failed lookup leaves every field editable and says so in place.
      console.error(e); setLookup('failed');
    } finally { setLookingUp(false); }
  }

  async function saveJob(event: FormEvent) {
    event.preventDefault();
    if (!activeCompanyId || !user || !isValid) return;
    setSaving(true); setError(null); setUploadProgress(null);
    try {
      // PR-26: the server owns state (always pending), jobNumber,
      // registrationKey, schemaVersion, createdBy, accountingStatus and the
      // jobCreated audit entry; evidence arrays are backend-only (invariant 2).
      const fields: Record<string, unknown> = {
        registrationNumber: registration, make: form.make.trim(), model: form.model.trim(),
        colour: form.colour.trim(), fuelType: form.fuelType.trim(), transmission: form.transmission.trim(),
        pickupLocation: form.pickupLocation.trim(), dropoffLocation: form.dropoffLocation.trim(),
        customerName: form.customerName.trim(), clientCreatedAt: Date.now(),
        courtesyCarRequired,
        ...contactPayload(form, companyFeatures.contactEmailEnabled),
      };
      const notes = form.notes.trim();
      if (notes) fields.notes = notes;
      // The booking goes as one unit; none means ASAP (PR-43).
      if (canBook && booking.date) Object.assign(fields, bookingFields(booking));
      // The casualty vehicle's mileage belongs to the vehicle, courtesy car or not (30 Oct 2026).
      const vehicleMileage = form.vehicleMileage.trim();
      if (vehicleMileage) fields.vehicleMileage = vehicleMileage;
      if (courtesyCarRequired) {
        const courtesyRegistration = form.courtesyCarRegistration.trim().toUpperCase();
        const courtesyMileage = form.courtesyCarMileage.trim();
        if (courtesyRegistration) fields.courtesyCarRegistration = courtesyRegistration;
        if (courtesyMileage) fields.courtesyCarMileage = courtesyMileage;
      }

      const created = await createJobCallable({
        companyId: activeCompanyId,
        jobId: draftJobId,
        idempotencyKey: `create-${draftJobId}`,
        fields,
      });
      const jobId = created.jobId;

      // createJob self-assigns drivers server-side; a dispatcher's chosen
      // assignee is applied as a follow-up call (same shape as iOS 3c-4).
      // The job already exists, so a failure here is reported as a follow-up
      // task rather than a failed save.
      let assignmentFailed = false;
      if (canAssign && assignmentMode !== 'clear') {
        try {
          const assign = httpsCallable<
            { companyId: string; jobId: string; assignmentType: string; assignedUserId?: string; assignedSubcontractorId?: string },
            { success: boolean }
          >(functions, 'assignRecoveryJob');
          if (assignmentMode === 'internal' && selectedAssignedUserId) {
            await assign({ companyId: activeCompanyId, jobId, assignmentType: 'internal', assignedUserId: selectedAssignedUserId });
          } else if (assignmentMode === 'subcontractor' && selectedAssignedSubcontractorId) {
            await assign({ companyId: activeCompanyId, jobId, assignmentType: 'subcontractor', assignedSubcontractorId: selectedAssignedSubcontractorId });
          }
        } catch (assignError) {
          console.warn('Assign-on-create failed', assignError);
          assignmentFailed = true;
        }
      }

      const failedUploads: string[] = [];
      for (let i = 0; i < files.length; i += 1) {
        setUploadProgress({ done: i, total: files.length });
        try {
          await uploadJobImage({ companyId: activeCompanyId, jobId, file: files[i], imageType });
        } catch (uploadError) {
          console.error('Photo upload failed after job creation', uploadError);
          failedUploads.push(files[i].name);
        }
      }
      sessionStorage.removeItem(NEW_JOB_ID_STORAGE_KEY);
      toast.success(`Job ${created.jobNumber} created.`);
      if (assignmentFailed) {
        toast.warning(
          'The job was created, but its assignment could not be applied. Set it from the job page.'
        );
      }
      if (failedUploads.length > 0) {
        toast.warning(
          `${failedUploads.length} photo${failedUploads.length === 1 ? '' : 's'} could not be uploaded. The job was saved; add the missing photo${failedUploads.length === 1 ? '' : 's'} from its Photos section.`
        );
      }
      navigate(`/jobs/${jobId}`);
    } catch (e) {
      console.error(e); setError(e instanceof Error ? e.message : String(e));
    } finally { setSaving(false); setUploadProgress(null); }
  }

  const totalOwed = missing.size;
  const photoTypes = evidenceUploadTypes(courtesyCarRequired);

  return (
    <form onSubmit={saveJob} className="pb-2">
      <Link to="/jobs" className="mb-2.5 inline-flex items-center gap-1 text-[15px] font-medium text-accent hover:underline">
        <ChevronLeft size={16} strokeWidth={2} /> Live jobs
      </Link>
      <h1 className="text-[32px] font-bold leading-[38px] tracking-[-0.03em] text-ink">New job</h1>
      <p className="mt-1.5 text-[15px] text-ink2">Only the registration is required. Everything else can be added now or later.</p>

      {!canCreate && <div className="mt-4"><Alert tone="error">Your role cannot create jobs in this company.</Alert></div>}
      {error && <div className="mt-4"><Alert tone="error">{error}</Alert></div>}
      {companyFeatures.error && <div className="mt-4"><Alert tone="warning">Company settings could not be loaded. Contact email is unavailable. <button type="button" onClick={companyFeatures.retry}>Try again</button></Alert></div>}

      <div className="mt-5 grid grid-cols-1 items-start gap-6 xl:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)]">
        <div className="flex flex-col gap-5">
          <Panel>
            <div className="p-5">
              <div className="eyebrow mb-2.5">Registration — all that is required</div>
              <RegPlate
                value={form.registrationNumber}
                onChange={(v) => { setField('registrationNumber', v); setLookup('idle'); }}
                onSearch={lookupRegistration}
                searching={lookingUp}
                autoFocus
              />
              <p className="mt-2.5 text-[13px] text-ink3">Spaces optional. Look up fills the vehicle from DVLA and MOT.</p>
            </div>
          </Panel>

          <Panel>
            {lookup === 'filled' && (
              <Strip tone="ok">Filled from DVLA and MOT<span className="ml-auto font-medium opacity-85">Edit anything that&rsquo;s wrong</span></Strip>
            )}
            {lookup === 'failed' && (
              <Strip tone="neutral">We couldn&rsquo;t look this registration up. Fill the vehicle in by hand.</Strip>
            )}
            <PanelTitle title="Vehicle" owed={vehicleOwed} />
            <div className="grid gap-4 p-5 pt-4 sm:grid-cols-2">
              <Field label="Make" htmlFor={ids.make} outstanding={owed('make')}>
                <TextInput id={ids.make} value={form.make} onChange={(e) => setField('make', e.target.value)} outstanding={owed('make')} />
              </Field>
              <Field label="Model" htmlFor={ids.model} outstanding={owed('model')}>
                <TextInput id={ids.model} value={form.model} onChange={(e) => setField('model', e.target.value)} outstanding={owed('model')} />
              </Field>
              <Field label="Colour" htmlFor={ids.colour} outstanding={owed('colour')}>
                <TextInput id={ids.colour} value={form.colour} onChange={(e) => setField('colour', e.target.value)} outstanding={owed('colour')} />
              </Field>
              <Field label="Fuel" htmlFor={ids.fuel} outstanding={owed('fuelType')}>
                <Select id={ids.fuel} value={form.fuelType} onChange={(e) => setField('fuelType', e.target.value)} outstanding={owed('fuelType')}>
                  {pickerOptions(FUEL_TYPES, form.fuelType).map((o) => <option key={o || 'e'} value={o}>{o || 'Choose fuel'}</option>)}
                </Select>
              </Field>
              <Field label="Transmission" htmlFor={ids.transmission} outstanding={owed('transmission')}>
                <Select id={ids.transmission} value={form.transmission} onChange={(e) => setField('transmission', e.target.value)} outstanding={owed('transmission')}>
                  {pickerOptions(TRANSMISSIONS, form.transmission).map((o) => <option key={o || 'e'} value={o}>{o || 'Choose transmission'}</option>)}
                </Select>
              </Field>
              <Field label="Mileage (optional)" htmlFor={ids.vehicleMileage}>
                <TextInput id={ids.vehicleMileage} value={form.vehicleMileage} inputMode="numeric" placeholder="Miles"
                  onChange={(e) => setField('vehicleMileage', e.target.value)} />
              </Field>
            </div>
          </Panel>

          <Panel>
            <PanelTitle title="Customer and route" owed={routeOwed} />
            <div className="grid gap-4 p-5 pt-4 sm:grid-cols-2">
              <Field label="Customer" htmlFor={ids.customer} outstanding={owed('customerName')} className="sm:col-span-2"
                hint={customerError ? <span className="text-st-cancelled">{customerError}</span> : undefined}>
                <CustomerPicker id={ids.customer} customers={customers} value={form.customerName} onChange={chooseCustomer} outstanding={owed('customerName')} />
              </Field>
              <div>
              <Field label="Collect from" htmlFor={ids.pickup} outstanding={owed('pickupLocation')}>
                <TextInput id={ids.pickup} value={form.pickupLocation} onChange={(e) => setField('pickupLocation', e.target.value)}
                  placeholder="Address or location" outstanding={owed('pickupLocation')} />
              </Field>
                <ContactFields kind="collection" value={form} onChange={setField} emailEnabled={companyFeatures.contactEmailEnabled} disabled={saving} />
              </div>
              <div>
              <Field label="Deliver to" htmlFor={ids.dropoff} outstanding={owed('dropoffLocation')}>
                <TextInput id={ids.dropoff} value={form.dropoffLocation} onChange={(e) => setField('dropoffLocation', e.target.value)}
                  placeholder="Address or location" outstanding={owed('dropoffLocation')} />
              </Field>
                <ContactFields kind="delivery" value={form} onChange={setField} emailEnabled={companyFeatures.contactEmailEnabled} disabled={saving} />
              </div>
            </div>
          </Panel>
        </div>

        <div className="flex flex-col gap-5">
          {canAssign && (
            <Panel>
              <PanelTitle title="Assignment" />
              <div className="flex flex-col gap-4 p-5 pt-4">
                <SegmentedControl label="Assign to" options={ASSIGNMENT_OPTIONS} value={assignmentMode} onChange={chooseAssignmentMode} />
                {assignmentMode === 'internal' && (
                  <Field label="Team member" htmlFor={ids.assignee}>
                    <Select id={ids.assignee} value={selectedAssignedUserId} onChange={(e) => setSelectedAssignedUserId(e.target.value)} disabled={loadingAssignmentOptions}>
                      <option value="">{loadingAssignmentOptions ? 'Loading…' : members.length === 0 ? 'No active team members' : 'Choose'}</option>
                      {members.map((m) => <option key={m.id} value={m.userId}>{m.displayName || m.email || m.userId}</option>)}
                    </Select>
                  </Field>
                )}
                {assignmentMode === 'subcontractor' && (
                  <Field label="Subcontractor" htmlFor={ids.assignee}>
                    <Select id={ids.assignee} value={selectedAssignedSubcontractorId} onChange={(e) => setSelectedAssignedSubcontractorId(e.target.value)} disabled={loadingAssignmentOptions}>
                      <option value="">{loadingAssignmentOptions ? 'Loading…' : subcontractors.length === 0 ? 'No active subcontractors' : 'Choose'}</option>
                      {subcontractors.map((sub) => <option key={sub.id} value={sub.id}>{sub.shortName || sub.name}</option>)}
                    </Select>
                  </Field>
                )}
                {assignmentError && <p className="text-[13px] text-st-cancelled">{assignmentError}</p>}
              </div>
            </Panel>
          )}

          <Panel>
            <div className="flex min-h-14 items-center justify-between gap-4 px-5 py-3.5">
              <div>
                <div className="text-[15.5px] font-semibold text-ink">Courtesy car supplied</div>
                <div className="mt-0.5 text-[13.5px] text-ink3">Adds a Courtesy Car evidence heading to this job.</div>
              </div>
              <Switch
                label="Courtesy car supplied"
                checked={courtesyCarRequired}
                onChange={(required) => {
                  setCourtesyCarRequired(required);
                  if (!required && imageType === 'courtesy') setImageType('additional');
                }}
              />
            </div>
            {courtesyCarRequired && (
              <div className="grid gap-4 border-t border-rowhair p-5 sm:grid-cols-2">
                <Field label="Courtesy car registration" htmlFor={ids.courtesyReg} outstanding={owed('courtesyCarRegistration')} className="sm:col-span-2">
                  <TextInput id={ids.courtesyReg} value={form.courtesyCarRegistration} placeholder="AB12 CDE" autoComplete="off"
                    onChange={(e) => setField('courtesyCarRegistration', e.target.value.toUpperCase())}
                    outstanding={owed('courtesyCarRegistration')} className="font-mono uppercase tracking-[0.04em]" />
                </Field>
                <Field label="Courtesy car mileage (optional)" htmlFor={ids.courtesyMileage}>
                  <TextInput id={ids.courtesyMileage} value={form.courtesyCarMileage} inputMode="numeric" placeholder="Miles"
                    onChange={(e) => setField('courtesyCarMileage', e.target.value)} />
                </Field>
                {courtesyOwed > 0 && <p className="text-[13px] text-ink3 sm:col-span-2">The courtesy car&rsquo;s registration is needed before the job can be completed.</p>}
              </div>
            )}
          </Panel>

          {canBook && (
            <Panel>
              <PanelTitle title="When" />
              <div className="p-5 pt-4">
                <BookingFields value={booking} onChange={setBooking} idPrefix="new-job-booking" />
              </div>
            </Panel>
          )}

          <Panel>
            <div className="p-5">
              <Field label="Notes" htmlFor={ids.notes} optional>
                <TextArea id={ids.notes} value={form.notes} onChange={(e) => setField('notes', e.target.value)} rows={4}
                  placeholder="Anything the driver needs to know" />
              </Field>
            </div>
          </Panel>

          <Panel>
            <PanelTitle title="Photographs" aside={<span className="text-[13px] text-ink3">Optional</span>} />
            <div
              onDragEnter={(e) => { e.preventDefault(); setIsDraggingPhotos(true); }}
              onDragOver={(e) => { e.preventDefault(); setIsDraggingPhotos(true); }}
              onDragLeave={() => setIsDraggingPhotos(false)}
              onDrop={handlePhotoDrop}
              className={`m-5 mt-4 flex flex-col items-center gap-3 rounded-[12px] border-[1.5px] border-dashed p-5 text-center transition-colors duration-[180ms] ease-out ${
                isDraggingPhotos ? 'border-accent bg-accenttint' : 'border-strong'
              }`}
            >
              <div role="radiogroup" aria-label="Evidence heading" className="flex flex-wrap justify-center gap-2">
                {photoTypes.map((type) => {
                  const selected = imageType === type.value;
                  return (
                    <button
                      key={type.value}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      onClick={() => setImageType(type.value)}
                      className={`rounded-full p-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent ${selected ? 'ring-2 ring-accent ring-offset-1 ring-offset-elev' : ''}`}
                    >
                      <PhotoTypeTag type={type.value} label={type.label} />
                    </button>
                  );
                })}
              </div>
              <ImagePlus size={22} strokeWidth={1.8} className="text-accent" aria-hidden />
              <div className="text-[15px] font-semibold text-ink">Drop photographs here</div>
              <label className={`relative ${buttonStyles('secondary', 'sm')} cursor-pointer focus-within:ring-2 focus-within:ring-accent`}>
                Choose files
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  className="sr-only left-0 top-0"
                  onChange={(e) => { addPhotoFiles(Array.from(e.target.files ?? [])); e.target.value = ''; }}
                />
              </label>
              <p className="text-[13px] text-ink3">Filed under the heading selected above. Camera capture stays on iOS.</p>
              {files.length > 0 && (
                <ul className="flex w-full flex-wrap justify-center gap-2 pt-1">
                  {files.map((file) => (
                    <li key={fileKey(file)} className="inline-flex max-w-full items-center gap-1.5 rounded-[8px] bg-search py-1 pl-2.5 pr-1 text-[13px] text-ink2">
                      <span className="max-w-56 truncate">{file.name}</span>
                      <button
                        type="button"
                        title={`Remove ${file.name}`}
                        aria-label={`Remove ${file.name}`}
                        disabled={saving}
                        onClick={() => setFiles((current) => current.filter((candidate) => fileKey(candidate) !== fileKey(file)))}
                        className="flex h-6 w-6 items-center justify-center rounded-[6px] text-ink3 hover:bg-hover hover:text-ink disabled:opacity-50"
                      >
                        <X size={14} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </Panel>
        </div>
      </div>

      <div className="sticky bottom-0 z-10 mt-7 flex flex-wrap items-center gap-x-5 gap-y-3 border-t border-hair bg-surface py-3.5">
        <Button type="submit" size="lg" disabled={!isValid} loading={saving} className="min-w-[220px]">
          {saving ? 'Creating…' : createBlockedReason ? `Create job — ${createBlockedReason}` : 'Create job'}
        </Button>
        <Link to="/jobs" className="text-[15px] font-medium text-accent hover:underline">Cancel</Link>
        <div className="ml-auto min-w-0 text-right text-[13px] text-ink3">
          {uploadProgress ? (
            <div className="flex min-w-[240px] flex-col gap-1.5">
              <span>Uploading photograph <span className="font-mono">{uploadProgress.done + 1} / {uploadProgress.total}</span></span>
              <span className="h-1.5 overflow-hidden rounded-full bg-sunken">
                <span className="block h-full rounded-full bg-st-progress transition-[width] duration-[180ms] ease-out"
                  style={{ width: `${(uploadProgress.done / uploadProgress.total) * 100}%` }} />
              </span>
            </div>
          ) : totalOwed > 0 ? (
            <>The job can be created now. <span className="text-outstanding">{totalOwed} detail{totalOwed === 1 ? '' : 's'}</span> will be needed before it can be completed.</>
          ) : registration ? 'Everything needed to complete is here.' : null}
        </div>
      </div>
    </form>
  );
}

function Panel({ children }: { children: ReactNode }) {
  return <section className="overflow-hidden rounded-[14px] border border-hair bg-elev">{children}</section>;
}

function PanelTitle({ title, owed = 0, aside }: { title: string; owed?: number; aside?: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 px-5 pt-4">
      <h2 className="text-[16.5px] font-semibold tracking-[-0.01em] text-ink">{title}</h2>
      {owed > 0 ? <OutstandingMark>{owed} needed to complete</OutstandingMark> : aside}
    </div>
  );
}

function Strip({ tone, children }: { tone: 'ok' | 'neutral'; children: ReactNode }) {
  return (
    <div className={`flex items-center gap-2 px-5 py-3 text-[13.5px] font-semibold ${tone === 'ok' ? 'bg-completedtint text-completedink' : 'bg-search text-ink2'}`}>
      <span className="h-[7px] w-[7px] shrink-0 rounded-full bg-current" aria-hidden />
      {children}
    </div>
  );
}

function stringFromUnknown(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null;
}

const NEW_JOB_ID_STORAGE_KEY = 'picup:new-job-id';

function getOrCreateDraftJobId(): string {
  const existing = sessionStorage.getItem(NEW_JOB_ID_STORAGE_KEY);
  if (existing && /^[A-Za-z0-9-]{8,64}$/.test(existing)) return existing;
  const next = crypto.randomUUID();
  sessionStorage.setItem(NEW_JOB_ID_STORAGE_KEY, next);
  return next;
}

function fileKey(file: File): string {
  return `${file.name}:${file.size}:${file.lastModified}`;
}
